import React from 'react';
import { X, MessageSquare, HeartPulse, FileText, Heart, Phone, Star, Shield, AlertTriangle } from 'lucide-react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white shadow-card w-full h-full sm:h-auto sm:max-h-[85vh] sm:max-w-2xl sm:rounded-card flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-rm-neutral-600/10 flex-shrink-0">
          <h2 className="text-2xl font-semibold text-rm-ink-900">
            Kim — Your Wellbeing Buddy
          </h2>
          <button
            onClick={onClose}
            className="text-rm-neutral-600 hover:text-rm-ink-900 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <p className="text-rm-ink-700">
            Kim is your personal digital wellbeing assistant, here to support you on your mental health journey. Whether you need someone to talk to, want to track how you're feeling, or are looking for helpful resources — Kim is available whenever you need it.
          </p>

          {/* Chat with Kim */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-rm-primary-700 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-rm-ink-900">Chat with Kim</h3>
            </div>
            <p className="text-sm text-rm-ink-700">
              The heart of the app is a friendly, conversational chat. Talk to Kim about anything related to your wellbeing — stress, work-life balance, sleep, focus, or just how your day is going. Kim listens, offers thoughtful advice, and shares helpful resources when relevant.
            </p>
            <p className="text-sm text-rm-ink-700">
              Each conversation is saved as a <strong>session</strong>, so you can pick up where you left off or start fresh anytime.
            </p>
          </div>

          {/* Wellbeing Check-in */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <HeartPulse className="w-5 h-5 text-rm-primary-700 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-rm-ink-900">Wellbeing Check-in</h3>
            </div>
            <p className="text-sm text-rm-ink-700">
              Kim regularly invites you to do a quick check-in across seven areas of your life:
            </p>
            <ul className="text-sm text-rm-ink-700 space-y-1 ml-4">
              <li><strong>Overall Wellbeing</strong> — How are you feeling today?</li>
              <li><strong>Stress Level</strong> — How stressed are you?</li>
              <li><strong>Work-Life Balance</strong> — Is life feeling balanced?</li>
              <li><strong>Focus</strong> — How well can you concentrate?</li>
              <li><strong>Energy</strong> — What's your energy like?</li>
              <li><strong>Social Support</strong> — Do you feel connected to others?</li>
              <li><strong>Sleep Quality</strong> — How have you been sleeping?</li>
            </ul>
            <p className="text-sm text-rm-ink-700">
              Over time these check-ins paint a picture of your wellbeing trends — helping you notice what's improving and what might need attention.
            </p>
          </div>

          {/* My Information */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-rm-primary-700 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-rm-ink-900">My Information</h3>
            </div>
            <p className="text-sm text-rm-ink-700">
              Visit <strong>My Information</strong> to see your wellbeing insights at a glance. View how your metrics have changed over different time periods — from the past week to the entire year.
            </p>
          </div>

          {/* My Resources */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-rm-primary-700 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-rm-ink-900">My Resources</h3>
            </div>
            <p className="text-sm text-rm-ink-700">
              Save videos, audio exercises, articles, and recipes that support your wellness to <strong>My Resources</strong> — your personal collection of go-to content. Search, filter by type, and see which resources you've used most.
            </p>
          </div>

          {/* Contact a Human */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-rm-primary-700 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-rm-ink-900">Contact a Human</h3>
            </div>
            <p className="text-sm text-rm-ink-700">
              Sometimes you need to talk to a real person, and that's completely okay. Tap the <strong>Contact Human</strong> button anytime to reach your Wellbeing Officer directly — by email, phone, or by booking a meeting.
            </p>
          </div>

          {/* Session Feedback */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-rm-primary-700 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-rm-ink-900">Session Feedback</h3>
            </div>
            <p className="text-sm text-rm-ink-700">
              After chatting with Kim, you can share feedback about your experience. Your input helps Kim get better at supporting you and others.
            </p>
          </div>

          {/* Getting Started */}
          <div className="space-y-3 pt-2 border-t border-rm-neutral-600/10">
            <h3 className="text-lg font-semibold text-rm-ink-900">Getting Started</h3>
            <ol className="text-sm text-rm-ink-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-rm-primary-700 text-white text-xs flex items-center justify-center mt-0.5">1</span>
                <span><strong>Sign in</strong> with your account — you'll see a warm welcome screen.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-rm-primary-700 text-white text-xs flex items-center justify-center mt-0.5">2</span>
                <span><strong>Set up your profile</strong> with a display name and review the privacy notice.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-rm-primary-700 text-white text-xs flex items-center justify-center mt-0.5">3</span>
                <span><strong>Start chatting</strong> with Kim, or do your first <strong>wellbeing check-in</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-rm-primary-700 text-white text-xs flex items-center justify-center mt-0.5">4</span>
                <span><strong>Explore resources</strong> and save your favourites.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-rm-primary-700 text-white text-xs flex items-center justify-center mt-0.5">5</span>
                <span><strong>Come back anytime</strong> — Kim is here whenever you need support.</span>
              </li>
            </ol>
          </div>

          {/* Privacy & Safety Notes */}
          <div className="space-y-3 pt-2">
            <div className="flex items-start gap-3 p-3 bg-rm-bg-50 rounded-lg border border-rm-neutral-600/10">
              <Shield className="w-5 h-5 text-rm-primary-700 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-rm-ink-700">
                <strong>Your privacy matters.</strong> Kim is designed with your privacy in mind. Your conversations and wellbeing data are kept confidential. You can review the full privacy notice at any time from within the app.
              </p>
            </div>
            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-rm-ink-700">
                <strong>Need immediate help?</strong> Kim is a supportive tool, not a replacement for professional care. If you're in crisis, please use the <strong>Contact Human</strong> button to reach your Wellbeing Officer or contact your local emergency services.
              </p>
            </div>
          </div>

          {/* Support Contact */}
          <p className="text-sm text-rm-ink-700 text-center pt-2 border-t border-rm-neutral-600/10">
            For support write to{' '}
            <a href="mailto:wellbeingofficer@renewmyday.com" className="text-rm-accent-600 hover:text-rm-accent-500 underline font-medium transition-colors">
              wellbeingofficer@renewmyday.com
            </a>{' '}
            or call{' '}
            <a href="tel:08008195" className="text-rm-accent-600 hover:text-rm-accent-500 underline font-medium transition-colors">
              0800 8195
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
